/**
 * The customer storefront runs on `HashLocationStrategy`, so a deep link's query
 * string can legitimately sit in either half of the address bar: before the `#`
 * (`/?salespoint=ABCD#l/1`) or embedded after a hash route segment
 * (`/#l/1?salespoint=ABCD`). Anything reading a URL-carried token has to look in
 * both places, because the table/location segments are hand-parsed by
 * `LocationService.loadView()` outside Angular's declared route tree.
 *
 * Reading only `location.search` is what broke card #11426: a table link with a
 * Sales Point code appended to it put that code inside the hash, the storefront
 * never found it, and the customer got the "This link is incomplete" gate instead
 * of the menu. `VoucherUtils.getVoucherCodeFromUrl` had already hit and closed the
 * same gap for `?voucher=`; these helpers hold the one implementation both use, so
 * the next parameter to need it cannot drift apart again.
 */

/**
 * The query string embedded after a hash route segment — `#l/1?a=b` -> `a=b`.
 * Returns `''` when the hash carries no query part.
 *
 * Everything after the *first* `?` is kept, not just up to the second one: a
 * value may legitimately carry an unencoded `?` (`#l/1?redirect=/x?y=z`), and
 * since `removeQueryParamFromUrl` re-serialises the hash from this string, a
 * `split('?')[1]` here would drop the remainder from the address bar for good
 * via `history.replaceState`. `URLSearchParams` parses `redirect=/x?y=z` as one
 * value, so the round-trip is safe. This also makes the split symmetric with the
 * `hash.indexOf('?')` already used for the route half below.
 */
export function getHashQuery(hash: string | null | undefined): string {
  return hash && hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
}

/**
 * Reads `param` out of one query string. A blank value counts as absent so the
 * caller falls through to the other half of the URL rather than stopping on an
 * empty `?salespoint=`.
 */
function readParam(query: string, param: string): string | null {
  if (!query) {
    return null;
  }
  return new URLSearchParams(query).get(param)?.trim() || null;
}

/**
 * Reads `param` from `search` first, then from a query string embedded in `hash`.
 * `search` wins when both carry a usable value, which keeps the pre-existing
 * plain-`?param=` behaviour untouched and makes the hash purely a fallback.
 */
export function getQueryParamFromUrl(
  search: string | null | undefined,
  hash: string | null | undefined,
  param: string,
): string | null {
  return readParam(search ?? '', param) ?? readParam(getHashQuery(hash), param);
}

/**
 * Removes `param` from wherever it actually sits in `href` — the plain query
 * string, the hash-embedded one, or both — and returns the rewritten
 * `pathname + search + hash`. The hash's route segment (`#l/<label>`) is always
 * preserved, so stripping a consumed token never costs the customer their table.
 *
 * Returns `null` when the parameter is not present anywhere, so callers can skip
 * a pointless history rewrite.
 */
export function removeQueryParamFromUrl(
  href: string,
  param: string,
): string | null {
  const url = new URL(href);
  const hashQuery = getHashQuery(url.hash);

  const inSearch = url.searchParams.has(param);
  const inHash = !!hashQuery && new URLSearchParams(hashQuery).has(param);
  if (!inSearch && !inHash) {
    return null;
  }

  if (inSearch) {
    url.searchParams.delete(param);
  }

  let hash = url.hash;
  if (inHash) {
    const route = url.hash.slice(0, url.hash.indexOf('?'));
    const remaining = new URLSearchParams(hashQuery);
    remaining.delete(param);
    const rest = remaining.toString();
    hash = rest ? `${route}?${rest}` : route;
  }

  return `${url.pathname}${url.search}${hash}`;
}
